import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import type { Me } from "./types";

export const meKey = ["auth", "me"] as const;

export function useMe() {
  return useQuery({
    queryKey: meKey,
    queryFn: () => api.get<Me>("/api/auth/me/"),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export interface LoginResult {
  status: "ok" | "2fa_required";
  user?: Me;
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { username: string; password: string }) => api.post<LoginResult>("/api/auth/login/", data),
    onSuccess: (res) => {
      if (res.status === "ok" && res.user) qc.setQueryData(meKey, res.user);
    },
  });
}

export function useVerify2fa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { code: string }) => api.post<LoginResult>("/api/auth/2fa/verify/", data),
    onSuccess: (res) => {
      if (res.user) qc.setQueryData(meKey, res.user);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/api/auth/logout/"),
    onSettled: () => {
      qc.clear();
      window.location.href = "/login";
    },
  });
}

export async function ensureCsrf() {
  try {
    await api.get("/api/auth/csrf/");
  } catch (e) {
    if (!(e instanceof ApiError)) throw e;
  }
}
